import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogFooter,
	ConfirmDeleteDialogHeader,
	ConfirmDeleteDialogTrigger,
} from "@/components/ui/confirm-delete-dialog";
import { useSession, useUpdateUser } from "@/hooks/auth-hooks";
import { authClient, signOut } from "@/lib/auth-client";
import { useWebHaptics } from "@/lib/haptics";

export const Route = createFileRoute("/dashboard/settings")({
	component: SettingsPage,
});


function SettingsPage() {
	const { user, refetch } = useSession();
	const navigate = useNavigate();
	const updateUser = useUpdateUser();
	const { trigger } = useWebHaptics();

	const accountsQuery = useQuery({
		enabled: !!user,
		queryKey: ["settings", "accounts"],
		queryFn: async () => {
			const result = await authClient.listAccounts();
			return result.data ?? [];
		},
	});
	const hasEmailAccount = accountsQuery.data?.some(
		(a) => a.providerId === "credential",
	) ?? false;

	const [name, setName] = useState(user?.name ?? "");
	const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
	const [newEmail, setNewEmail] = useState("");
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
	const [notifJobEmail, setNotifJobEmail] = useState(true);
	const [isSavingNotifications, setIsSavingNotifications] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);

	const notificationsQuery = useQuery({
		enabled: !!user,
		queryKey: ["settings", "notifications"],
		queryFn: async () => {
			const res = await fetch("/api/settings/notifications", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch notification settings");
			return res.json() as Promise<{
				settings: { notifyOnJobCompletionEmail: boolean };
			}>;
		},
	});

	useEffect(() => {
		if (
			typeof notificationsQuery.data?.settings?.notifyOnJobCompletionEmail ===
			"boolean"
		) {
			setNotifJobEmail(
				notificationsQuery.data.settings.notifyOnJobCompletionEmail,
			);
		}
	}, [notificationsQuery.data?.settings?.notifyOnJobCompletionEmail]);

	const handleUpdateProfile = async () => {
		if (!name.trim()) return;
		setIsUpdatingProfile(true);
		try {
			await updateUser.mutateAsync({ name: name.trim() });
			await refetch();
			trigger("success");
			toast.success("Profile updated");
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update profile",
			);
		} finally {
			setIsUpdatingProfile(false);
		}
	};

	const handleUpdateEmail = async () => {
		if (!newEmail.trim() || newEmail.trim() === user?.email) return;
		try {
			const result = await authClient.changeEmail({
				newEmail: newEmail.trim(),
			});
			if (result.error) {
				toast.error(result.error.message || "Failed to update email");
			} else {
				trigger("success");
				await refetch();
				setNewEmail("");
				toast.success("Email update requested. Check your inbox to verify.");
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update email",
			);
		}
	};

	const handleUpdatePassword = async (e: FormEvent) => {
		e.preventDefault();
		if (!currentPassword || !newPassword || !confirmPassword) return;
		if (newPassword !== confirmPassword) {
			toast.error("New passwords do not match");
			return;
		}
		if (newPassword.length < 8) {
			toast.error("Password must be at least 8 characters");
			return;
		}
		setIsUpdatingPassword(true);
		try {
			const result = await authClient.changePassword({
				currentPassword,
				newPassword,
			});
			if (result.error) {
				toast.error(result.error.message || "Failed to update password");
			} else {
				trigger("success");
				setCurrentPassword("");
				setNewPassword("");
				setConfirmPassword("");
				toast.success("Password updated");
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update password",
			);
		} finally {
			setIsUpdatingPassword(false);
		}
	};

	const handleSaveNotifications = async () => {
		setIsSavingNotifications(true);
		try {
			const res = await fetch("/api/settings/notifications", {
				method: "PUT",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					notifyOnJobCompletionEmail: notifJobEmail,
				}),
			});
			const payload = (await res.json()) as {
				error?: string;
				settings?: { notifyOnJobCompletionEmail: boolean };
			};
			if (!res.ok) {
				throw new Error(
					payload.error || "Failed to save notification settings",
				);
			}
			setNotifJobEmail(
				payload.settings?.notifyOnJobCompletionEmail ?? notifJobEmail,
			);
			await notificationsQuery.refetch();
			trigger("success");
			toast.success("Notification settings updated");
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: "Failed to save notification settings",
			);
		} finally {
			setIsSavingNotifications(false);
		}
	};

	const handleDeleteAccount = async () => {
		if (deleteConfirmText !== "DELETE") return;
		setIsDeleting(true);
		try {
			const result = await authClient.deleteUser();
			if (result.error) {
				toast.error(result.error.message || "Failed to delete account");
			} else {
				setDeleteDialogOpen(false);
				await signOut();
				navigate({ to: "/" });
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to delete account",
			);
		} finally {
			setIsDeleting(false);
		}
	};

	const muted = "#adaaaa";
	const borderSoft = "rgba(255,255,255,0.06)";
	const surface = "#131313";
	const border = "rgba(255,255,255,0.08)";

	return (
		<DashboardLayout>
			{/* Page header */}
				<div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
					<div>
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Dashboard
						</p>
						<h1
							className="text-5xl font-semibold tracking-tighter mb-2"
							style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
						>
							Settings
						</h1>
						<p
							className="text-sm"
							style={{ color: muted }}
						>
							Manage your account preferences
						</p>
					</div>
					<div
						className="hidden lg:block h-px flex-1 mx-8 mb-4"
						style={{
							background: "linear-gradient(to right, rgba(234,40,4,0.3), transparent)",
						}}
					/>
				</div>

				<div className="space-y-12">
					{/* Profile */}
					<div>
						<div className="flex items-center gap-4 mb-6">
							<h2
								className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
								style={{ fontFamily: "inherit", color: "#6B3FF2" }}
							>
								Profile
							</h2>
							<div className="h-px flex-1" style={{ backgroundColor: borderSoft }} />
						</div>
						<div
							className="p-6"
							style={{ backgroundColor: surface, border: `1px solid ${border}`, borderTop: "3px solid #6B3FF2" }}
						>
							<div className="grid gap-6 md:grid-cols-2">
								<div className="space-y-2">
									<label
										htmlFor="settings-name"
										className="block text-[10px] uppercase tracking-wide font-bold"
										style={{ fontFamily: "inherit", color: muted }}
									>
										Full Name
									</label>
									<input
										id="settings-name"
										type="text"
										value={name}
										onChange={(e) => setName(e.target.value)}
										className="w-full px-4 py-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
										style={{
											backgroundColor: "var(--km-surface)",
											border: `1px solid ${border}`,
											color: "var(--km-on-surface)",
										}}
									/>
								</div>
								<div className="space-y-2">
									<label
										htmlFor="settings-email"
										className="block text-[10px] uppercase tracking-wide font-bold"
										style={{ fontFamily: "inherit", color: muted }}
									>
										Email Address
									</label>
									<input
										id="settings-email"
										type="email"
										value={newEmail || user?.email || ""}
										onChange={(e) => setNewEmail(e.target.value)}
										className="w-full px-4 py-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
										style={{
											backgroundColor: "var(--km-surface)",
											border: `1px solid ${border}`,
											color: "var(--km-on-surface)",
										}}
									/>
								</div>
							</div>
							<div className="mt-6 flex justify-end gap-3">
								<button
									type="button"
									onClick={() => { setName(user?.name ?? ""); setNewEmail(""); }}
									className="text-xs font-bold uppercase tracking-wide px-4 py-2.5 transition-colors hover:text-[var(--km-on-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{ fontFamily: "inherit", color: muted }}
								>
									Discard
								</button>
								<button
									type="button"
									disabled={isUpdatingProfile || !name.trim()}
									onClick={() => { void handleUpdateProfile(); void handleUpdateEmail(); }}
									className="px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-50 transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{ fontFamily: "inherit", backgroundColor: "#6B3FF2", color: "#ffffff" }}
								>
									{isUpdatingProfile ? "Saving…" : "Save Profile"}
								</button>
							</div>
						</div>
					</div>

					{/* Notifications */}
					<div>
						<div className="flex items-center gap-4 mb-6">
							<h2
								className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
								style={{ fontFamily: "inherit", color: "#6B3FF2" }}
							>
								Notifications
							</h2>
							<div className="h-px flex-1" style={{ backgroundColor: borderSoft }} />
						</div>
						<div
							className="p-6"
							style={{ backgroundColor: surface, border: `1px solid ${border}`, borderTop: "3px solid #6B3FF2" }}
						>
							<div className="flex items-center justify-between gap-6">
								<div>
									<p className="text-sm font-bold" style={{ color: "var(--km-on-surface)" }}>
										Job completion emails
									</p>
									<p className="mt-1 text-xs" style={{ color: muted }}>
										Receive an email with a direct file link when a long-running generation finishes.
									</p>
								</div>
								<button
									type="button"
									role="switch"
									aria-checked={notifJobEmail}
									aria-label="Toggle job completion email notifications"
									disabled={notificationsQuery.isLoading || isSavingNotifications}
									onClick={() => setNotifJobEmail((v) => !v)}
									className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full disabled:opacity-60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#131313]"
									style={{ backgroundColor: notifJobEmail ? "#6B3FF2" : "#262626" }}
								>
									<span
										className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform"
										style={{ left: notifJobEmail ? "calc(100% - 22px)" : "2px" }}
									/>
								</button>
							</div>
							<div className="mt-6 flex justify-end">
								<button
									type="button"
									onClick={() => { void handleSaveNotifications(); }}
									disabled={notificationsQuery.isLoading || isSavingNotifications}
									className="px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-50 transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{ fontFamily: "inherit", backgroundColor: "#6B3FF2", color: "#ffffff" }}
								>
									{isSavingNotifications ? "Saving…" : "Save"}
								</button>
							</div>
						</div>
					</div>

					{/* Security, email/password users only */}
					{hasEmailAccount && <div>
						<div className="flex items-center gap-4 mb-6">
							<h2
								id="security-heading"
								className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
								style={{ fontFamily: "inherit", color: "#6B3FF2" }}
							>
								Security
							</h2>
							<div className="h-px flex-1" style={{ backgroundColor: borderSoft }} />
						</div>
						<form
							onSubmit={handleUpdatePassword}
							aria-labelledby="security-heading"
							className="p-6"
							style={{ backgroundColor: surface, border: `1px solid ${border}`, borderTop: "3px solid #6B3FF2" }}
						>
							<div className="space-y-5">
								{[
									{ id: "settings-current-password", label: "Current Password", value: currentPassword, onChange: setCurrentPassword, autoComplete: "current-password" },
									{ id: "settings-new-password", label: "New Password", value: newPassword, onChange: setNewPassword, autoComplete: "new-password" },
									{ id: "settings-confirm-password", label: "Confirm New Password", value: confirmPassword, onChange: setConfirmPassword, autoComplete: "new-password" },
								].map(({ id, label, value, onChange, autoComplete }) => (
									<div key={id} className="space-y-2">
										<label
											htmlFor={id}
											className="block text-[10px] uppercase tracking-wide font-bold"
											style={{ fontFamily: "inherit", color: muted }}
										>
											{label}
										</label>
										<input
											id={id}
											type="password"
											autoComplete={autoComplete}
											value={value}
											onChange={(e) => onChange(e.target.value)}
											className="w-full px-4 py-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
											style={{
												backgroundColor: "var(--km-surface)",
												border: `1px solid ${border}`,
												color: "var(--km-on-surface)",
											}}
										/>
									</div>
								))}
								<div className="flex justify-end pt-1">
									<button
										type="submit"
										disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword}
										aria-busy={isUpdatingPassword}
										className="px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-50 transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
										style={{ fontFamily: "inherit", backgroundColor: "#6B3FF2", color: "#ffffff" }}
									>
										{isUpdatingPassword ? "Updating…" : "Update Password"}
									</button>
								</div>
							</div>
						</form>
					</div>}

					{/* Danger Zone */}
					<div>
						<div className="flex items-center gap-4 mb-6">
							<h2
								className="text-xs uppercase tracking-[0.1em] font-bold shrink-0"
								style={{ fontFamily: "inherit", color: "#6B3FF2" }}
							>
								Danger Zone
							</h2>
							<div className="h-px flex-1" style={{ backgroundColor: borderSoft }} />
						</div>
						<div
							className="p-6"
							style={{ backgroundColor: "rgba(255,110,132,0.05)", border: "1px solid #ff6e84", borderTop: "3px solid #ff6e84" }}
						>
							<p className="mb-5 text-sm" style={{ color: muted }}>
								Permanently delete your account and all associated data. This cannot be undone.
							</p>
							<ConfirmDeleteDialog
								open={deleteDialogOpen}
								onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(""); }}
							>
								<ConfirmDeleteDialogTrigger asChild>
									<button
										type="button"
										className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6e84]/60"
										style={{
											fontFamily: "inherit",
											backgroundColor: "rgba(255,110,132,0.1)",
											color: "#ff6e84",
											border: "1px solid #ff6e84",
										}}
									>
										Delete Account
									</button>
								</ConfirmDeleteDialogTrigger>
								<ConfirmDeleteDialogContent>
									<ConfirmDeleteDialogHeader
										title="Delete Account"
										description="This will permanently delete your account and all your data. This action cannot be undone."
									/>
									<ConfirmDeleteDialogConfirmation
										value={deleteConfirmText}
										onChange={setDeleteConfirmText}
										confirmText="DELETE"
										placeholder="Type DELETE"
									/>
									<ConfirmDeleteDialogFooter
										onCancel={() => { setDeleteDialogOpen(false); setDeleteConfirmText(""); }}
										onConfirm={handleDeleteAccount}
										confirmDisabled={deleteConfirmText !== "DELETE"}
										isLoading={isDeleting}
										confirmLabel="Delete My Account"
									/>
								</ConfirmDeleteDialogContent>
							</ConfirmDeleteDialog>
						</div>
					</div>
			</div>
		</DashboardLayout>
	);
}
